/**
 * media.ts — responsive <picture> helpers
 *
 * For MVP we expect content authors to supply AVIF + WebP + JPEG variants when
 * possible. `vite-imagetools` can produce these from a single source via query
 * strings; for MDX-authored content the simplest convention is to ship all
 * three variants at the same basename and let this helper render the picture.
 */

export interface PictureSources {
  /** Preferred path (any format). Used to derive sibling variants. */
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  loading?: 'eager' | 'lazy';
  fetchPriority?: 'auto' | 'high' | 'low';
}

export function deriveVariants(src: string): { avif?: string; webp?: string; fallback: string } {
  const dot = src.lastIndexOf('.');
  if (dot === -1) return { fallback: src };
  const stem = src.slice(0, dot);
  const ext = src.slice(dot + 1).toLowerCase();

  // SVG is resolution-independent; no need for AVIF/WebP siblings.
  // Inventing them confuses <picture> when the siblings don't exist.
  if (ext === 'svg') return { fallback: src };

  if (ext === 'avif') return { avif: src, fallback: `${stem}.jpg` };
  if (ext === 'webp') return { webp: src, fallback: `${stem}.jpg` };

  // Raster source — assume AVIF + WebP siblings exist alongside.
  return {
    avif: `${stem}.avif`,
    webp: `${stem}.webp`,
    fallback: src,
  };
}
